import supabase from '../utils/supabase';
import { escapePostgresLike } from '../utils/sqlSecurity';

class ChatService {
  /**
   * Start a new support conversation
   */
  async startConversation(userId, subject, category = 'general', priority = 'normal') {
    try {
      const { data, error } = await supabase
        .from('chat_conversations')
        .insert({
          user_id: userId,
          subject,
          category,
          priority,
          status: 'open'
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] startConversation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Send a message in a conversation
   */
  async sendMessage(conversationId, senderId, messageText, isAdmin = false, attachments = null) {
    try {
      // Update conversation's updated_at timestamp (fire and forget - don't block on this)
      supabase
        .from('chat_conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', conversationId)
        .then(({ error }) => {
          if (error) {
            console.error('[ChatService] Failed to update conversation timestamp:', error);
          }
        });

      const insertData = {
        conversation_id: conversationId,
        sender_id: senderId,
        message_text: messageText,
        is_admin: isAdmin,
        attachments
      };
      
      // Use raw fetch to avoid Supabase client hanging issue
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      // Get access token from localStorage synchronously (avoid supabase.auth.getSession() which hangs)
      let token = supabaseKey;
      try {
        const sessionData = localStorage.getItem('va-sprouts-auth-storage');
        if (sessionData) {
          const parsed = JSON.parse(sessionData);
          if (parsed?.access_token) {
            token = parsed.access_token;
          }
        }
      } catch (err) {
        console.warn('[ChatService] Could not read token from storage, using anon key:', err);
      }
      
      const response = await fetch(`${supabaseUrl}/rest/v1/chat_messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseKey,
          'Authorization': `Bearer ${token}`,
          'Prefer': 'return=representation'
        },
        body: JSON.stringify(insertData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ChatService] Insert failed:', response.status, errorText);
        throw new Error(`Insert failed: ${response.status} ${errorText}`);
      }
      
      // Parse the response to get the inserted message data
      const responseData = await response.json();
      const insertedMessage = Array.isArray(responseData) ? responseData[0] : responseData;
      
      console.log('[ChatService] Message sent successfully:', insertedMessage?.id);
      
      return { success: true, data: insertedMessage };
    } catch (error) {
      console.error('[ChatService] sendMessage error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user's conversations
   */
  async getUserConversations(userId, limit = 50) {
    try {
      console.log('[ChatService] getUserConversations called for user:', userId);
      
      // Simplified query without joins to avoid RLS/performance issues
      const { data, error } = await supabase
        .from('chat_conversations')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false })
        .limit(limit);

      console.log('[ChatService] getUserConversations query completed:', { 
        success: !error, 
        count: data?.length,
        error: error?.message 
      });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] getUserConversations:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get all conversations for admin
   */
  async getAllConversations(status = null, priority = null, limit = 100) {
    try {
      let query = supabase
        .from('chat_conversations')
        .select(`
          *,
          admin:admin_id(id, full_name, profile_picture),
          user:user_id(id, full_name, profile_picture, email),
          chat_messages(count)
        `)
        .order('updated_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      if (priority) {
        query = query.eq('priority', priority);
      }

      const { data, error } = await query.limit(limit);

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] getAllConversations:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get conversation messages
   */
  async getConversationMessages(conversationId, limit = 100) {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select(`
          *,
          sender:sender_id(id, full_name, profile_picture, role)
        `)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true })
        .limit(limit);

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] getConversationMessages:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Mark message as read
   */
  async markMessageAsRead(messageId) {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .update({ read_at: new Date().toISOString() })
        .eq('id', messageId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] markMessageAsRead:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Mark all messages in conversation as read
   */
  async markConversationAsRead(conversationId, userId) {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .update({ read_at: new Date().toISOString() })
        .eq('conversation_id', conversationId)
        .neq('sender_id', userId)
        .is('read_at', null);

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] markConversationAsRead:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update conversation status
   */
  async updateConversationStatus(conversationId, status, adminId = null) {
    try {
      const updateData = { 
        status, 
        updated_at: new Date().toISOString()
      };
      
      if (adminId) {
        updateData.admin_id = adminId;
      }
      
      if (status === 'resolved') {
        updateData.resolved_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('chat_conversations')
        .update(updateData)
        .eq('id', conversationId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] updateConversationStatus:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update conversation priority
   */
  async updateConversationPriority(conversationId, priority) {
    try {
      if (!['low', 'normal', 'high', 'urgent'].includes(priority)) {
        throw new Error('Invalid priority value');
      }

      const { data, error } = await supabase
        .from('chat_conversations')
        .update({ 
          priority, 
          updated_at: new Date().toISOString()
        })
        .eq('id', conversationId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] updateConversationPriority:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Archive a conversation
   */
  async archiveConversation(conversationId, adminId) {
    try {
      const { data, error } = await supabase
        .from('chat_conversations')
        .update({
          is_archived: true,
          archived_at: new Date().toISOString(),
          archived_by: adminId,
          updated_at: new Date().toISOString()
        })
        .eq('id', conversationId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] archiveConversation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Assign conversation to admin
   */
  async assignConversation(conversationId, adminId) {
    try {
      const { data, error } = await supabase
        .from('chat_conversations')
        .update({ 
          admin_id: adminId,
          status: 'in_progress',
          updated_at: new Date().toISOString()
        })
        .eq('id', conversationId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] assignConversation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Claim conversation for moderator
   * This is normally done automatically via database trigger when moderator sends first message
   */
  async claimConversation(conversationId, moderatorId) {
    try {
      const { data, error } = await supabase
        .from('chat_conversations')
        .update({ 
          claimed_by: moderatorId,
          claimed_at: new Date().toISOString(),
          status: 'in_progress',
          updated_at: new Date().toISOString()
        })
        .eq('id', conversationId)
        .is('claimed_by', null) // Only claim if not already claimed
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] claimConversation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Release conversation claim (admin only, via RPC function)
   */
  async releaseConversationClaim(conversationId) {
    try {
      const { data, error } = await supabase
        .rpc('release_conversation_claim', { p_conversation_id: conversationId });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] releaseConversationClaim:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get moderator's claimed conversations
   */
  async getModeratorConversations(moderatorId) {
    try {
      const { data, error } = await supabase
        .from('chat_conversations')
        .select(`
          *,
          user:user_id(id, full_name, profile_picture, email),
          claimed_moderator:claimed_by(id, full_name)
        `)
        .eq('claimed_by', moderatorId)
        .neq('is_archived', true)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] getModeratorConversations:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get available (unclaimed) conversations for moderators
   */
  async getAvailableConversations() {
    try {
      const { data, error } = await supabase
        .from('chat_conversations')
        .select(`
          *,
          user:user_id(id, full_name, profile_picture, email)
        `)
        .is('claimed_by', null)
        .neq('is_archived', true)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: true }); // Oldest first

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] getAvailableConversations:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Rate conversation (customer satisfaction)
   * @param {string} conversationId - The conversation ID to rate
   * @param {number} rating - Rating value (1-5)
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async rateConversation(conversationId, rating) {
    try {
      if (!conversationId) {
        throw new Error('Conversation ID is required');
      }

      if (rating < 1 || rating > 5) {
        throw new Error('Rating must be between 1 and 5');
      }

      console.log('[ChatService] rateConversation:', { conversationId, rating });

      const { data, error } = await supabase
        .from('chat_conversations')
        .update({ satisfaction_rating: rating, updated_at: new Date().toISOString() })
        .eq('id', conversationId)
        .select();

      if (error) {
        console.error('[ChatService] rateConversation - Supabase error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          conversationId
        });
        throw error;
      }

      // Check if update actually affected any rows
      if (!data || data.length === 0) {
        console.warn('[ChatService] rateConversation - No rows updated:', { conversationId });
        return { 
          success: false, 
          error: 'Conversation not found or you do not have permission to rate it. Please check that you are rating your own conversation.'
        };
      }

      console.log('[ChatService] rateConversation - Success:', { conversationId, rating });
      return { success: true, data: data[0] };
    } catch (error) {
      console.error('[ChatService] rateConversation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create support ticket
   */
  async createTicket(userId, conversationId, title, description, category = 'general', priority = 'normal') {
    try {
      // Generate ticket number
      const ticketNumber = `TKT-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

      const { data, error } = await supabase
        .from('support_tickets')
        .insert({
          user_id: userId,
          conversation_id: conversationId,
          ticket_number: ticketNumber,
          title,
          description,
          priority,
          status: 'open'
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] createTicket:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get support tickets for user
   */
  async getUserTickets(userId) {
    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .select(`
          *,
          assigned_admin:assigned_to(id, full_name, profile_picture, email)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] getUserTickets:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get all support tickets for admin
   */
  async getAllTickets(status = null, assignedTo = null) {
    try {
      let query = supabase
        .from('support_tickets')
        .select(`
          *,
          user:user_id(id, full_name, profile_picture, email),
          assigned_admin:assigned_to(id, full_name, profile_picture, email)
        `)
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      if (assignedTo) {
        query = query.eq('assigned_to', assignedTo);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] getAllTickets:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update support ticket
   */
  async updateTicket(ticketId, updates) {
    try {
      const updateData = { ...updates };
      
      if (updates.status === 'resolved') {
        updateData.resolved_at = new Date().toISOString();
      }

      // Do the update and return basic ticket data
      const { data, error } = await supabase
        .from('support_tickets')
        .update(updateData)
        .eq('id', ticketId)
        .select('*')
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] updateTicket:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get support analytics for admin dashboard
   */
  async getSupportAnalytics() {
    try {
      // Get conversation stats
      const { data: conversations, error: convError } = await supabase
        .from('chat_conversations')
        .select('status, priority, satisfaction_rating, created_at, resolved_at');

      if (convError) throw convError;

      // Calculate metrics
      const totalConversations = conversations?.length || 0;
      const openConversations = conversations?.filter(c => c.status === 'open').length || 0;
      const inProgressConversations = conversations?.filter(c => c.status === 'in_progress').length || 0;
      const resolvedConversations = conversations?.filter(c => c.status === 'resolved').length || 0;
      
      // Average satisfaction
      const ratings = conversations?.filter(c => c.satisfaction_rating)?.map(c => c.satisfaction_rating) || [];
      const avgSatisfaction = ratings.length > 0 
        ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(2)
        : 0;

      // Average resolution time
      const resolvedWithTime = conversations?.filter(c => c.resolved_at && c.created_at) || [];
      const avgResolutionTime = resolvedWithTime.length > 0
        ? (resolvedWithTime.reduce((acc, c) => {
            const time = new Date(c.resolved_at) - new Date(c.created_at);
            return acc + time;
          }, 0) / resolvedWithTime.length / (1000 * 60 * 60)).toFixed(2)
        : 0;

      // Urgency breakdown
      const urgentCount = conversations?.filter(c => c.priority === 'urgent').length || 0;
      const highCount = conversations?.filter(c => c.priority === 'high').length || 0;

      return {
        success: true,
        data: {
          totalConversations,
          openConversations,
          inProgressConversations,
          resolvedConversations,
          avgSatisfaction: parseFloat(avgSatisfaction),
          avgResolutionTimeHours: parseFloat(avgResolutionTime),
          urgentPriority: urgentCount,
          highPriority: highCount,
          resolutionRate: totalConversations > 0 
            ? ((resolvedConversations / totalConversations) * 100).toFixed(2)
            : 0
        }
      };
    } catch (error) {
      console.error('[ChatService] getSupportAnalytics:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Search conversations
   */
  async searchConversations(query, field = 'subject') {
    try {
      const escapedQuery = escapePostgresLike(query.toLowerCase());
      const { data, error } = await supabase
        .from('chat_conversations')
        .select(`
          *,
          user:user_id(id, full_name, email),
          admin:admin_id(id, full_name)
        `)
        .or(`${field}.ilike.%${escapedQuery}%,user.full_name.ilike.%${escapedQuery}%,user.email.ilike.%${escapedQuery}%`)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] searchConversations:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get unread message count for conversation
   */
  async getUnreadCount(conversationId, userId) {
    try {
      const { count, error } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .eq('conversation_id', conversationId)
        .neq('sender_id', userId)
        .is('read_at', null);

      if (error) throw error;
      return { success: true, count: count || 0 };
    } catch (error) {
      console.error('[ChatService] getUnreadCount:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get ticket with full conversation history
   */
  async getTicketWithConversation(ticketId) {
    try {
      // Get ticket details
      const { data: ticket, error: ticketError } = await supabase
        .from('support_tickets')
        .select(`
          *,
          user:user_id(id, full_name, email, profile_picture),
          assigned_admin:assigned_to(id, full_name, profile_picture),
          conversation:conversation_id(
            id,
            subject,
            status,
            created_at,
            updated_at
          )
        `)
        .eq('id', ticketId)
        .single();

      if (ticketError) throw ticketError;

      // Get conversation messages if conversation exists
      let messages = [];
      if (ticket.conversation_id) {
        const messagesResult = await this.getConversationMessages(ticket.conversation_id);
        if (messagesResult.success) {
          messages = messagesResult.data || [];
        }
      }

      return { 
        success: true, 
        data: {
          ...ticket,
          messages
        }
      };
    } catch (error) {
      console.error('[ChatService] getTicketWithConversation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Add or update resolution notes for a ticket
   */
  async addResolutionNotes(ticketId, notes, adminId) {
    try {
      // Get existing resolution notes to append
      const { data: existingTicket, error: fetchError } = await supabase
        .from('support_tickets')
        .select('resolution_notes')
        .eq('id', ticketId)
        .single();

      if (fetchError) throw fetchError;

      // Append new notes with timestamp and admin info
      const timestamp = new Date().toISOString();
      const newNote = `\n[${timestamp}] Admin ${adminId}: ${notes}`;
      const updatedNotes = (existingTicket.resolution_notes || '') + newNote;

      const { data, error } = await supabase
        .from('support_tickets')
        .update({ 
          resolution_notes: updatedNotes,
          updated_at: timestamp
        })
        .eq('id', ticketId)
        .select('*')
        .single();

      if (error) throw error;

      // Fetch with relationships
      const { data: fullData, error: fetchError2 } = await supabase
        .from('support_tickets')
        .select(`
          *,
          user:user_id(id, full_name, profile_picture, email),
          assigned_admin:assigned_to(id, full_name, profile_picture, email)
        `)
        .eq('id', ticketId)
        .single();

      if (fetchError2) throw fetchError2;
      return { success: true, data: fullData };
    } catch (error) {
      console.error('[ChatService] addResolutionNotes:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Bulk assign tickets to an admin
   */
  async bulkAssignTickets(ticketIds, adminId) {
    try {
      const { error } = await supabase
        .from('support_tickets')
        .update({ 
          assigned_to: adminId,
          updated_at: new Date().toISOString()
        })
        .in('id', ticketIds);

      if (error) throw error;

      // Fetch updated tickets with relationships
      const { data, error: fetchError } = await supabase
        .from('support_tickets')
        .select(`
          *,
          user:user_id(id, full_name, profile_picture, email),
          assigned_admin:assigned_to(id, full_name, profile_picture, email)
        `)
        .in('id', ticketIds);

      if (fetchError) throw fetchError;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] bulkAssignTickets:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get tickets filtered by tags
   */
  async getTicketsByTag(tags, adminView = false) {
    try {
      let query = supabase
        .from('support_tickets')
        .select(`
          *,
          user:user_id(id, full_name, email, profile_picture),
          assigned_admin:assigned_to(id, full_name, profile_picture)
        `)
        .contains('tags', tags)
        .order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] getTicketsByTag:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Archive a ticket (mark as closed with resolution)
   */
  async archiveTicket(ticketId, resolutionNotes = null) {
    try {
      const updateData = {
        status: 'closed',
        resolved_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (resolutionNotes) {
        updateData.resolution_notes = resolutionNotes;
      }

      const { error } = await supabase
        .from('support_tickets')
        .update(updateData)
        .eq('id', ticketId);

      if (error) throw error;

      // Fetch with relationships
      const { data, error: fetchError } = await supabase
        .from('support_tickets')
        .select(`
          *,
          user:user_id(id, full_name, profile_picture, email),
          assigned_admin:assigned_to(id, full_name, profile_picture, email)
        `)
        .eq('id', ticketId)
        .single();

      if (fetchError) throw fetchError;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] archiveTicket:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Reopen a closed ticket
   */
  async reopenTicket(ticketId) {
    try {
      const { error } = await supabase
        .from('support_tickets')
        .update({ 
          status: 'open',
          resolved_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', ticketId);

      if (error) throw error;

      // Fetch with relationships
      const { data, error: fetchError } = await supabase
        .from('support_tickets')
        .select(`
          *,
          user:user_id(id, full_name, profile_picture, email),
          assigned_admin:assigned_to(id, full_name, profile_picture, email)
        `)
        .eq('id', ticketId)
        .single();

      if (fetchError) throw fetchError;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] reopenTicket:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get ticket-specific analytics for admin dashboard
   */
  async getTicketAnalytics() {
    try {
      const { data: tickets, error } = await supabase
        .from('support_tickets')
        .select('status, priority, created_at, resolved_at, tags');

      if (error) throw error;

      // Calculate metrics
      const totalTickets = tickets?.length || 0;
      const openTickets = tickets?.filter(t => t.status === 'open').length || 0;
      const inProgressTickets = tickets?.filter(t => t.status === 'in_progress').length || 0;
      const resolvedTickets = tickets?.filter(t => t.status === 'resolved').length || 0;
      const closedTickets = tickets?.filter(t => t.status === 'closed').length || 0;

      // Priority breakdown
      const urgentTickets = tickets?.filter(t => t.priority === 'urgent').length || 0;
      const highTickets = tickets?.filter(t => t.priority === 'high').length || 0;
      const normalTickets = tickets?.filter(t => t.priority === 'normal').length || 0;
      const lowTickets = tickets?.filter(t => t.priority === 'low').length || 0;

      // Average resolution time for tickets
      const resolvedWithTime = tickets?.filter(t => t.resolved_at && t.created_at) || [];
      const avgResolutionTime = resolvedWithTime.length > 0
        ? (resolvedWithTime.reduce((acc, t) => {
            const time = new Date(t.resolved_at) - new Date(t.created_at);
            return acc + time;
          }, 0) / resolvedWithTime.length / (1000 * 60 * 60)).toFixed(2)
        : 0;

      // Tag frequency
      const tagCounts = {};
      tickets?.forEach(ticket => {
        ticket.tags?.forEach(tag => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        });
      });

      return {
        success: true,
        data: {
          totalTickets,
          openTickets,
          inProgressTickets,
          resolvedTickets,
          closedTickets,
          urgentTickets,
          highTickets,
          normalTickets,
          lowTickets,
          avgTicketResolutionTimeHours: parseFloat(avgResolutionTime),
          tagDistribution: tagCounts,
          resolutionRate: totalTickets > 0 
            ? (((resolvedTickets + closedTickets) / totalTickets) * 100).toFixed(2)
            : 0
        }
      };
    } catch (error) {
      console.error('[ChatService] getTicketAnalytics:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create a support ticket from an existing conversation
   * Links the ticket to the conversation for unified support management
   * @param {string} conversationId - ID of the conversation to escalate
   * @param {object} ticketData - Ticket details (title, description, priority, tags)
   * @returns {object} { success: boolean, data: ticket, error: string }
   */
  async createTicketFromConversation(conversationId, ticketData) {
    try {
      // Fetch conversation details
      const { data: conversation, error: convError } = await supabase
        .from('chat_conversations')
        .select('id, user_id, subject, created_at')
        .eq('id', conversationId)
        .single();

      if (convError || !conversation) {
        throw new Error('Conversation not found');
      }

      // Generate unique ticket number (TKT-TIMESTAMP)
      const ticketNumber = `TKT-${Date.now().toString().slice(-8)}`;

      // Create ticket linked to conversation
      const { data: ticket, error: ticketError } = await supabase
        .from('support_tickets')
        .insert([
          {
            conversation_id: conversationId,
            user_id: conversation.user_id,
            ticket_number: ticketNumber,
            title: ticketData.title || conversation.subject,
            description: ticketData.description || '',
            status: 'open',
            priority: ticketData.priority || 'normal',
            tags: ticketData.tags || [],
            created_at: new Date().toISOString()
          }
        ])
        .select()
        .single();

      if (ticketError) throw ticketError;

      return { success: true, data: ticket };
    } catch (error) {
      console.error('[ChatService] createTicketFromConversation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete a conversation and all related messages (admin only)
   * This is a permanent operation and cannot be undone
   * @param {string} conversationId - ID of the conversation to delete
   * @returns {object} { success: boolean, data: number, error: string }
   */
  async deleteConversation(conversationId) {
    try {
      console.log('[ChatService] deleteConversation called:', conversationId);
      
      // First delete all messages in this conversation
      const { error: messagesError } = await supabase
        .from('chat_messages')
        .delete()
        .eq('conversation_id', conversationId);

      if (messagesError) {
        console.error('[ChatService] Error deleting messages:', messagesError);
        throw messagesError;
      }

      console.log('[ChatService] Messages deleted successfully');

      // Then delete the conversation
      const { data, error: conversationError } = await supabase
        .from('chat_conversations')
        .delete()
        .eq('id', conversationId)
        .select();

      if (conversationError) {
        console.error('[ChatService] Error deleting conversation:', conversationError);
        throw conversationError;
      }

      console.log('[ChatService] Conversation deleted successfully:', data);
      return { success: true, data: conversationId };
    } catch (error) {
      console.error('[ChatService] deleteConversation:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get available admins and moderators for ticket assignment
   */
  async getAvailableAdmins() {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, role, profile_picture')
        .in('role', ['admin', 'moderator'])
        .order('full_name', { ascending: true });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[ChatService] getAvailableAdmins:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Assign or unassign ticket to admin/moderator with email notification (only for assignment)
   * @param {string} ticketId - ID of the ticket to assign
   * @param {string|null} adminId - ID of the admin/moderator to assign to, or null to unassign
   * @param {object} ticketContext - Ticket context data for email (simple object, not full ticketData)
   * @returns {object} { success: boolean, data: ticket, error: string }
   */
  async assignTicketToAdmin(ticketId, adminId, ticketContext = {}) {
    try {
      const isUnassigning = adminId === null || adminId === '' || adminId === undefined;
      
      console.log('[ChatService] assignTicketToAdmin:', { ticketId, adminId, isUnassigning });

      // Do the update and get basic ticket data
      const { data: updatedTicket, error: updateError } = await supabase
        .from('support_tickets')
        .update({ 
          assigned_to: isUnassigning ? null : adminId,
          updated_at: new Date().toISOString()
        })
        .eq('id', ticketId)
        .select('*')
        .single();

      if (updateError) {
        console.error('[ChatService] Update error:', updateError.message);
        throw updateError;
      }

      console.log('[ChatService] Ticket updated successfully');

      // Send email notification only when assigning (not unassigning)
      if (!isUnassigning) {
        // Fetch admin data for email
        const { data: adminData } = await supabase
          .from('user_profiles')
          .select('id, full_name, email')
          .eq('id', adminId)
          .single();

        if (adminData) {
          try {
            const { error: emailError } = await supabase.functions.invoke('send-email', {
              body: {
                emailType: 'ticket-assigned',
                email: adminData.email,
                fullName: adminData.full_name,
                ticketNumber: ticketContext?.ticket_number || updatedTicket.ticket_number,
                ticketTitle: ticketContext?.title || updatedTicket.title,
                ticketPriority: ticketContext?.priority || updatedTicket.priority,
                ticketStatus: ticketContext?.status || updatedTicket.status,
                customerName: ticketContext?.user?.full_name || 'Customer',
                ticketUrl: `https://dashboard.vasprouts.com/admin/support?ticket=${ticketId}`,
                description: ticketContext?.description || updatedTicket.description || ''
              }
            });

            if (emailError) {
              console.error('[ChatService] Failed to send assignment email (non-critical):', emailError.message);
            } else {
              console.log('[ChatService] Assignment email sent successfully');
            }
          } catch (emailError) {
            console.error('[ChatService] Email notification error (non-critical):', emailError.message);
          }
        }
      }

      return { success: true, data: updatedTicket };
    } catch (error) {
      console.error('[ChatService] assignTicketToAdmin error:', error.message);
      return { success: false, error: error.message };
    }
  }
}

export default new ChatService();
